import { Typography, Box, Fade, Popover, ClickAwayListener } from "@mui/material";
import { Theme } from "@mui/material/styles";
import React, { useCallback, useState } from "react";

/** A color, or a resolver for one from the theme, as sx accepts. */
export type ThemeColor = string | ((theme: Theme) => string);

const HOVER_HALO_PX = 8;
// Content inset, in MUI spacing units; also how far an extended popover is
// pulled back so its text lands on the anchor's text.
const CONTENT_PX = 2;
const CONTENT_PY = 1;

// When extending an anchor, cover its full width so none of its clipped text peeks out.
const paperMinWidth = (extendedAnchor: HTMLElement | null) =>
  extendedAnchor
    ? (theme: Theme) => `calc(${extendedAnchor.offsetWidth}px + ${theme.spacing(2 * CONTENT_PX)})`
    : 200;

interface MiniModalProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  width?: number | string;
  maxHeight?: string;
  anchorEl: HTMLElement | null;
  /** Match the surface the anchor sits on so the popover reads as that field lifting out. */
  backgroundColor?: ThemeColor;
  /** Close as soon as the pointer leaves the popover, not only on an outside click. */
  closeOnMouseLeave?: boolean;
  /**
   * Pull the popover back by its own padding so its title and value sit exactly
   * on the anchor's, reading as the anchor growing in place rather than a box
   * appearing beside it. For anchors whose text starts at their top-left.
   */
  extendAnchor?: boolean;
}

export const MiniModal: React.FC<MiniModalProps> = ({
  open,
  onClose,
  title,
  children,
  width,
  maxHeight = "80vh",
  anchorEl,
  backgroundColor,
  closeOnMouseLeave = false,
  extendAnchor = false,
}) => {
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      // Extended popovers fade; the default grow scales from the corner, which
      // reads as jitter when the popover sits on top of its anchor.
      slots={extendAnchor ? { transition: Fade } : undefined}
      anchorOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      PaperProps={{
        onMouseLeave: closeOnMouseLeave ? onClose : undefined,
        sx: {
          // Invisible halo so a small overshoot past the edge doesn't close it.
          // Behind the content so the value's own click still copies.
          ...(closeOnMouseLeave && {
            "&::after": {
              content: '""',
              position: "absolute",
              inset: `-${HOVER_HALO_PX}px`,
              zIndex: -1,
            },
          }),
          minWidth: paperMinWidth(extendAnchor ? anchorEl : null),
          maxWidth: { xs: "90vw", sm: "50vw", md: "35vw" },
          width: width || "auto",
          maxHeight,
          backgroundColor,
          ...(extendAnchor && { mt: -CONTENT_PY, ml: -CONTENT_PX }),
          boxShadow: 3,
          borderRadius: 1,
          overflow: "visible", // Allow content to determine size
          // Ensure proper width calculation for wrapped text
          display: "inline-block",
        },
      }}
    >
      <ClickAwayListener onClickAway={onClose}>
        <Box
          sx={{
            px: CONTENT_PX,
            py: CONTENT_PY,
            position: "relative",
            minHeight: 40,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {title && (
            <Typography
              variant="body2"
              fontSize="0.75rem"
              color="text.secondary"
              sx={{
                mb: extendAnchor ? 0 : 0.5,
                pr: 4,
                flexShrink: 0, // Don't shrink the title
                wordBreak: "break-word",
                overflowWrap: "break-word",
                hyphens: "auto",
                lineHeight: extendAnchor ? 1.43 : 1.3,
              }}
            >
              {title}
            </Typography>
          )}
          <Box
            sx={{
              flex: 1,
              minHeight: 0, // Allow flex item to shrink below content size
              wordBreak: "break-word",
              whiteSpace: "pre-wrap",
              overflowWrap: "break-word", // Additional break-word support
              hyphens: "auto", // Allow hyphenation for better wrapping
              overflow: "auto", // Handle overflow within content area
              maxHeight: "60vh", // Limit content area height
              lineHeight: extendAnchor ? 1.5 : 1.4,
            }}
          >
            {children}
          </Box>
        </Box>
      </ClickAwayListener>
    </Popover>
  );
};

export const useMiniModal = (enabled = true) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const openModal = useCallback(
    (event: React.SyntheticEvent<HTMLElement>) => {
      if (!enabled) {
        return;
      }
      setAnchorEl(event.currentTarget);
      setModalOpen(true);
    },
    [enabled]
  );

  const closeModal = useCallback(() => {
    if (!enabled) {
      return;
    }
    setModalOpen(false);
    setAnchorEl(null);
  }, [enabled]);

  return {
    modalOpen,
    anchorEl,
    openModal,
    closeModal,
  };
};
